import React, { useId } from "react";
import { type ProductItemCard } from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import { VariantDropdown } from "@/components/common/VariantDropdown/VariantDropdown";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./BundleContents.module.css";

export interface BundleContentsProps {
  /** The products this bundle puts in the cart, in CMS order. */
  items: ProductItemCard[];
  /** Chosen variant id per bundled product id. */
  selectedVariants: Record<string, string>;
  onSelectVariant: (productId: string, variantId: string) => void;
  /** Localised "Includes" label from the CMS, when the bundle supplied one. */
  includesLabel?: string | null;
}

/**
 * What is in the bundle, listed on the bundle's own page.
 *
 * A bundled product sold in several versions needs its version chosen here — the
 * bundle add sends one order line per included product, and a line with no
 * variant carries no size/colour. Products without variants are shown as plain
 * rows.
 */
export const BundleContents: React.FC<BundleContentsProps> = ({
  items,
  selectedVariants,
  onSelectVariant,
  includesLabel,
}) => {
  const localizedTexts = useLocalizedTexts();
  const headingId = useId();

  if (!items.length) return null;

  const heading =
    includesLabel?.trim() ||
    t(localizedTexts, "Tracker.Generic.Includes", "Includes");

  return (
    <section className={styles.contents} data-component="bundle-contents">
      <h3 className={styles.heading} id={headingId}>
        {heading}
      </h3>
      <ul className={styles.items} aria-labelledby={headingId}>
        {items.map((item) => (
          <li key={item.id} className={styles.item}>
            <div className={styles.itemHeader}>
              {item.image?.url ? (
                <img
                  {...fixedCmsImage(item.image.url, { width: 48 })}
                  alt={item.image.altText || item.header}
                  className={styles.thumb}
                  width={48}
                  height={48}
                  loading="lazy"
                />
              ) : (
                <span className={styles.thumbPlaceholder} aria-hidden="true" />
              )}
              <span className={styles.itemName}>{item.header}</span>
            </div>

            {item.variants?.length > 0 && (
              // Nothing preselected: the version has to be a deliberate choice,
              // and Add-to-cart stays disabled until every bundled product has one.
              <div className={styles.variantRow}>
                <VariantDropdown
                  variants={item.variants}
                  selectedVariantId={selectedVariants[item.id] ?? null}
                  onSelect={(variantId) => onSelectVariant(item.id, variantId)}
                  productName={item.header}
                  tone="pdp"
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BundleContents;
